import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { normalizeProductUnit } from "@shared/productUnit";
import { CUSTOMER_TYPE_PRICE_MODES } from "@shared/schema";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import {
  CUSTOMER_TYPES_PATH,
  customerTypePath,
  formatPercent,
  isHiddenByType,
  type CustomerTypeDetail,
  type CustomerTypeSummary,
} from "@/lib/customerTypes";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import { UnitPill } from "@/components/unit-pill";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CatalogProduct {
  id: number;
  name: string;
  categoryId: number;
  isActive: boolean;
  unit?: string | null;
}

interface CatalogCategory {
  id: number;
  name: string;
}

const ALL_UNITS = "__all";
const NO_UNIT = "__none";

/**
 * Settings → Master Menu → Customer Types: groups of customers (grocers, restaurants)
 * with their own menu and prices (BRIEF-customer-types-zones-picksheet.md §2a). The
 * server applies them (server/services/menuPricing.ts); an org with no types serves
 * the master menu at its prices, exactly as before types existed.
 */
export function CustomerTypesCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Keyed by the org-scoped path, so one org's cached types never show under another's.
  const listKey = [getApiPath(CUSTOMER_TYPES_PATH)];
  const { data: types, isLoading, isError, refetch, isRefetching } = useQuery<CustomerTypeSummary[]>({ queryKey: listKey });
  const { data: products = [] } = useQuery<CatalogProduct[]>({ queryKey: ["/api/products"] });
  const { data: categories = [] } = useQuery<CatalogCategory[]>({ queryKey: ["/api/categories"] });

  // null = closed; { id: null } = a new type.
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [deleting, setDeleting] = useState<CustomerTypeSummary | null>(null);

  const remove = useMutation({
    mutationFn: async (type: CustomerTypeSummary) => {
      await apiRequest("DELETE", customerTypePath(type.id));
    },
    onSuccess: (_data, type) => {
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.removeQueries({ queryKey: [getApiPath(customerTypePath(type.id))] });
      // Its accounts fell back to no type.
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      toast({ title: `${type.name} deleted`, description: "Its customers see the master menu at base prices." });
      setDeleting(null);
    },
    onError: (error) => {
      toast({
        title: "Couldn't delete the customer type",
        description: parseApiError(error).message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <Card data-testid="card-customer-types">
      <CardHeader className="border-b border-gray-100 bg-gray-50/50">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Customer Types</CardTitle>
            <CardDescription className="mt-[5px] mb-[5px]">
              Give a group of customers — grocers, restaurants — their own menu and prices. A customer with no type
              sees the master menu at its prices.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={isLoading || isError}
            data-testid="button-add-customer-type"
            onClick={() => setEditing({ id: null })}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add a type
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : isError || !types ? (
          <div className="flex items-center justify-between gap-4" data-testid="customer-types-load-error">
            <p className="text-sm text-red-600">Couldn't load the customer types.</p>
            <Button type="button" variant="outline" size="sm" disabled={isRefetching} onClick={() => refetch()}>
              {isRefetching && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Try again
            </Button>
          </div>
        ) : types.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="customer-types-empty">
            No customer types yet. Add one to hide categories or products from a group of customers, or to give them
            their own prices. Then set each customer's type on their account page.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="list-customer-types">
            {types.map((type) => {
              const facts = [
                type.ruleCount > 0 ? `${type.ruleCount} hidden ${type.ruleCount === 1 ? "product or category" : "products or categories"}` : null,
                type.priceMode === "own"
                  ? type.priceCount > 0
                    ? `${type.priceCount} own ${type.priceCount === 1 ? "price" : "prices"}`
                    : "Own prices — none set yet"
                  : null,
                type.priceMode === "percent" && type.priceAdjustPercent != null
                  ? `${formatPercent(type.priceAdjustPercent)} on master menu prices`
                  : null,
              ].filter(Boolean);
              return (
                <li key={type.id} className="flex items-center justify-between gap-4 py-3" data-testid={`row-customer-type-${type.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">
                      {type.name}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        {type.accountCount} {type.accountCount === 1 ? "customer" : "customers"}
                      </span>
                    </p>
                    <p className="text-sm text-gray-500">{facts.length > 0 ? facts.join(" · ") : "Master menu and prices — nothing set yet"}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${type.name}`}
                      data-testid={`button-edit-customer-type-${type.id}`}
                      onClick={() => setEditing({ id: type.id })}
                    >
                      <Pencil className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${type.name}`}
                      data-testid={`button-delete-customer-type-${type.id}`}
                      onClick={() => setDeleting(type)}
                    >
                      <Trash2 className="h-4 w-4 text-gray-500" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {editing && (
        <CustomerTypeEditor
          key={editing.id ?? "new"}
          typeId={editing.id}
          products={products}
          categories={categories}
          onClose={() => setEditing(null)}
        />
      )}

      <AlertDialog open={deleting !== null} onOpenChange={(open) => { if (!open && !remove.isPending) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && deleting.accountCount > 0
                ? `Its ${deleting.accountCount} ${deleting.accountCount === 1 ? "customer goes" : "customers go"} back to the master menu at base prices. `
                : ""}
              Its hidden products and its own prices are deleted with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={remove.isPending}
              data-testid="button-confirm-delete-customer-type"
              onClick={(e) => {
                e.preventDefault();
                if (deleting) remove.mutate(deleting);
              }}
            >
              {remove.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

const typeFormSchema = z
  .object({
    name: z.string().trim().min(1, "Name the type.").max(100, "Keep the name to 100 characters."),
    priceMode: z.enum(CUSTOMER_TYPE_PRICE_MODES),
    priceAdjustPercent: z.string().trim(),
  })
  .superRefine((values, ctx) => {
    // The percent is checked only when it is the price in use; otherwise it is hidden and
    // not sent, so the stored one stays as it was.
    if (values.priceMode !== "percent") return;
    const v = values.priceAdjustPercent;
    if (!(/^[-+]?\d+(\.\d+)?$/.test(v) && Number(v) > -100 && Number(v) <= 1000)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["priceAdjustPercent"],
        message: "A percent above −100 and up to 1000, like −7.",
      });
    }
  });
type TypeForm = z.infer<typeof typeFormSchema>;

function CustomerTypeEditor({
  typeId,
  products,
  categories,
  onClose,
}: {
  typeId: number | null;
  products: CatalogProduct[];
  categories: CatalogCategory[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // A new type becomes an existing one at its first POST: a retry after a failed rules
  // save must PATCH it, never create a second.
  const [savedId, setSavedId] = useState<number | null>(typeId);
  const detailKey = [getApiPath(customerTypePath(typeId ?? 0))];
  const { data: detail, isLoading, isError } = useQuery<CustomerTypeDetail>({
    queryKey: detailKey,
    enabled: typeId !== null,
  });

  const form = useForm<TypeForm>({
    resolver: zodResolver(typeFormSchema),
    defaultValues: { name: "", priceMode: "base", priceAdjustPercent: "" },
  });
  const [hiddenProducts, setHiddenProducts] = useState<Set<number>>(new Set());
  const [hiddenCategories, setHiddenCategories] = useState<Set<number>>(new Set());
  const [initialRules, setInitialRules] = useState<string>("");
  const [ready, setReady] = useState(typeId === null);
  const [unitFilter, setUnitFilter] = useState<string>(ALL_UNITS);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!detail || ready) return;
    form.reset({
      name: detail.name,
      priceMode: detail.priceMode,
      priceAdjustPercent: detail.priceAdjustPercent == null ? "" : String(detail.priceAdjustPercent),
    });
    const productIds = new Set<number>();
    const categoryIds = new Set<number>();
    for (const rule of detail.rules) {
      if (!rule.hidden) continue;
      if (rule.productId != null) productIds.add(rule.productId);
      else if (rule.categoryId != null) categoryIds.add(rule.categoryId);
    }
    setHiddenProducts(productIds);
    setHiddenCategories(categoryIds);
    setInitialRules(rulesSignature(productIds, categoryIds));
    setReady(true);
  }, [detail, ready, form]);

  // The org's sell units, most-used first: a filter for the list below, nothing more.
  const units = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products) {
      const unit = p.unit ? normalizeProductUnit(p.unit) : null;
      if (unit && p.isActive) counts.set(unit, (counts.get(unit) ?? 0) + 1);
    }
    return Array.from(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [products]);
  const hasUnitless = products.some((p) => !p.unit);

  const typePreview = useMemo(
    () => ({
      rules: [
        ...Array.from(hiddenProducts).map((productId) => ({ productId, categoryId: null, hidden: true })),
        ...Array.from(hiddenCategories).map((categoryId) => ({ productId: null, categoryId, hidden: true })),
      ],
    }),
    [hiddenProducts, hiddenCategories],
  );
  const activeProducts = products.filter((p) => p.isActive);
  const hiddenActiveCount = activeProducts.filter((p) => isHiddenByType(p, typePreview)).length;

  const needle = search.trim().toLowerCase();
  const shownProducts = products.filter((p) => {
    if (unitFilter === NO_UNIT ? !!p.unit : unitFilter !== ALL_UNITS && normalizeProductUnit(p.unit ?? "") !== unitFilter) return false;
    return needle === "" || p.name.toLowerCase().includes(needle);
  });
  const shownByCategory = categories
    .map((category) => ({ category, items: shownProducts.filter((p) => p.categoryId === category.id) }))
    .filter((group) => group.items.length > 0);

  // On this type's menu: not off by its category or its own tick.
  const isOn = (p: CatalogProduct) => !hiddenCategories.has(p.categoryId) && !hiddenProducts.has(p.id);
  // What "Turn off all listed" turns off: listed products still on.
  const hideableShown = shownProducts.filter(isOn);

  const toggleSet = <T,>(set: (fn: (prev: Set<T>) => Set<T>) => void, value: T, on: boolean) =>
    set((prev) => {
      const next = new Set(prev);
      if (on) next.add(value);
      else next.delete(value);
      return next;
    });

  // A product's tick: unticked is a hide rule, ticked is no rule.
  const setProductsOn = (list: CatalogProduct[], on: boolean) => {
    const hide = new Set(hiddenProducts);
    for (const p of list) {
      if (on) hide.delete(p.id);
      else hide.add(p.id);
    }
    setHiddenProducts(hide);
  };

  const save = useMutation({
    mutationFn: async (values: TypeForm) => {
      const body = {
        name: values.name.trim(),
        priceMode: values.priceMode,
        // Sent only in percent mode: switching away keeps the stored percent, unused.
        ...(values.priceMode === "percent" ? { priceAdjustPercent: Number(values.priceAdjustPercent) } : {}),
      };
      let id = savedId;
      if (id === null) {
        const res = await apiRequest("POST", CUSTOMER_TYPES_PATH, body);
        id = ((await res.json()) as { id: number }).id;
        setSavedId(id);
      } else {
        await apiRequest("PATCH", customerTypePath(id), body);
      }
      const rulesNow = rulesSignature(hiddenProducts, hiddenCategories);
      if (rulesNow !== initialRules) {
        await apiRequest("PUT", `${customerTypePath(id)}/rules`, {
          rules: [
            ...Array.from(hiddenProducts).map((productId) => ({ productId })),
            ...Array.from(hiddenCategories).map((categoryId) => ({ categoryId })),
          ],
        });
        setInitialRules(rulesNow);
      }
      return { id, name: body.name };
    },
    onSuccess: ({ id, name }) => {
      queryClient.invalidateQueries({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
      queryClient.invalidateQueries({ queryKey: [getApiPath(customerTypePath(id))] });
      toast({ title: `${name} saved`, description: "Its customers see the change the next time their menu loads." });
      onClose();
    },
    onError: (error) => {
      // Whatever part did save shows in the list.
      queryClient.invalidateQueries({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
      const parsed = parseApiError(error);
      if (parsed.status === 409) {
        form.setError("name", { message: parsed.message ?? "A customer type with that name already exists." });
        return;
      }
      toast({
        title: "Couldn't save the customer type",
        description: parsed.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: TypeForm) => save.mutate(values);
  const priceMode = form.watch("priceMode");
  const storedPrices = detail?.prices.length ?? 0;

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !save.isPending) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-customer-type">
        <DialogHeader>
          <DialogTitle>{typeId === null ? "New customer type" : `Edit ${detail?.name ?? "customer type"}`}</DialogTitle>
          <DialogDescription>
            What customers of this type see, and what they pay.
          </DialogDescription>
        </DialogHeader>

        {typeId !== null && (isLoading || !ready) ? (
          isError ? (
            <p className="text-sm text-red-600">Couldn't load this customer type. Close and try again.</p>
          ) : (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )
        ) : (
          <form
            id="customer-type-form"
            className="space-y-6"
            onSubmit={form.handleSubmit(onSubmit, (errors) => console.error("🚫 RHF validation failed:", errors))}
          >
            <div className="space-y-1 sm:max-w-sm">
              <Label htmlFor="customer-type-name">Name</Label>
              <Input id="customer-type-name" placeholder="e.g., Grocer" maxLength={100} data-testid="input-customer-type-name" {...form.register("name")} />
              {form.formState.errors.name && <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label>Prices</Label>
              <RadioGroup
                value={priceMode}
                onValueChange={(value) => form.setValue("priceMode", value as TypeForm["priceMode"], { shouldDirty: true })}
                className="gap-3"
                data-testid="radio-customer-type-price-mode"
              >
                <label className="flex cursor-pointer items-start gap-3">
                  <RadioGroupItem value="base" className="mt-0.5" data-testid="radio-price-mode-base" />
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">Same as the master menu</span>
                    <span className="block text-gray-500">This type pays the master menu's prices.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <RadioGroupItem value="own" className="mt-0.5" data-testid="radio-price-mode-own" />
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">Own prices</span>
                    <span className="block text-gray-500">
                      A price column for this type appears in the Master Menu. A product left blank uses the master
                      menu price.
                      {storedPrices > 0 ? ` ${storedPrices} ${storedPrices === 1 ? "price is" : "prices are"} set.` : ""}
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <RadioGroupItem value="percent" className="mt-0.5" data-testid="radio-price-mode-percent" />
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">Percent adjustment</span>
                    <span className="block text-gray-500">Every master menu price, moved by a percent.</span>
                  </span>
                </label>
              </RadioGroup>
              {priceMode === "percent" && (
                <div className="space-y-1 pl-7">
                  <div className="flex items-center gap-2">
                    <Input
                      id="customer-type-percent"
                      aria-label="Percent adjustment"
                      inputMode="decimal"
                      placeholder="e.g., -7"
                      className="w-28"
                      data-testid="input-customer-type-percent"
                      {...form.register("priceAdjustPercent")}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  {form.formState.errors.priceAdjustPercent ? (
                    <p className="text-sm text-red-600">{form.formState.errors.priceAdjustPercent.message}</p>
                  ) : (
                    <p className="text-xs text-gray-500">−7 is 7% below the master menu price; 5 is 5% above.</p>
                  )}
                </div>
              )}
              {priceMode !== "own" && storedPrices > 0 && (
                <p className="text-xs text-gray-500" data-testid="text-customer-type-prices-kept">
                  This type's {storedPrices} own {storedPrices === 1 ? "price is kept, unused, and comes" : "prices are kept, unused, and come"}{" "}
                  back if you switch to Own prices.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-1">
                  <Label>Products and categories on this menu</Label>
                  <p className="text-sm text-gray-500" data-testid="text-customer-type-hidden-count">
                    Shows {activeProducts.length - hiddenActiveCount} of {activeProducts.length} active products.
                    Uncheck a category or a product to hide it from this type. A product you add later shows to this
                    type until you uncheck it here or on the product.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search products"
                    aria-label="Search products"
                    className="pl-8"
                    data-testid="input-customer-type-search"
                  />
                </div>
                <Select value={unitFilter} onValueChange={setUnitFilter}>
                  <SelectTrigger className="sm:w-40" aria-label="Filter by sell unit" data-testid="select-customer-type-unit-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_UNITS}>All units</SelectItem>
                    {units.map(([unit]) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                    {hasUnitless && <SelectItem value={NO_UNIT}>No unit</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={hideableShown.length === 0}
                  data-testid="button-hide-all-shown"
                  onClick={() => setProductsOn(hideableShown, false)}
                >
                  Turn off all listed ({hideableShown.length})
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!shownProducts.some((p) => hiddenProducts.has(p.id))}
                  data-testid="button-unhide-all-shown"
                  onClick={() => {
                    const shown = new Set(shownProducts.map((p) => p.id));
                    setHiddenProducts((prev) => new Set(Array.from(prev).filter((id) => !shown.has(id))));
                  }}
                >
                  Turn on all listed
                </Button>
              </div>
              {/* `relative` is load-bearing: inside a form each Radix Checkbox renders an absolutely
                  positioned hidden input. Without a positioned ancestor here they escape this
                  scroller and stretch the dialog's own scroll by the full list height. */}
              <div className="relative max-h-72 overflow-y-auto rounded-md border p-2 space-y-2" data-testid="customer-type-checklist">
                {shownByCategory.length === 0 ? (
                  <p className="p-2 text-sm text-gray-500">No products match.</p>
                ) : (
                  shownByCategory.map(({ category, items }) => {
                    const categoryHidden = hiddenCategories.has(category.id);
                    return (
                      <div key={category.id} className="rounded-sm border">
                        <label className="flex cursor-pointer items-center gap-2 bg-gray-50 p-2">
                          <Checkbox
                            checked={!categoryHidden}
                            onCheckedChange={(checked) => toggleSet(setHiddenCategories, category.id, checked !== true)}
                            data-testid={`checkbox-hide-category-${category.id}`}
                          />
                          <span className={`text-sm font-medium ${categoryHidden ? "text-gray-400" : ""}`}>{category.name}</span>
                          <span className="text-xs text-gray-500">{categoryHidden ? "whole category hidden" : `${items.length} listed`}</span>
                        </label>
                        <div className="space-y-0.5 p-1">
                          {items.map((product) => {
                            const on = isOn(product);
                            return (
                              <label
                                key={product.id}
                                className={`flex items-center gap-2 rounded px-1 py-1 pl-6 ${categoryHidden ? "cursor-default" : "cursor-pointer hover:bg-gray-50"}`}
                              >
                                <Checkbox
                                  checked={on}
                                  disabled={categoryHidden}
                                  onCheckedChange={(checked) => setProductsOn([product], checked === true)}
                                  data-testid={`checkbox-hide-product-${product.id}`}
                                />
                                <span className={`text-sm ${product.isActive && on ? "text-gray-800" : "text-gray-400"}`}>
                                  {product.name}
                                  <UnitPill unit={product.unit} />
                                </span>
                                {!product.isActive && <span className="text-xs text-gray-400">inactive</span>}
                                {categoryHidden && <span className="text-xs text-amber-700">hidden with category</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" disabled={save.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="customer-type-form"
            className="bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green"
            disabled={save.isPending || !ready}
            data-testid="button-save-customer-type"
          >
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function rulesSignature(productIds: Set<number>, categoryIds: Set<number>): string {
  const sorted = (s: Set<number>) => Array.from(s).sort((a, b) => a - b).join(",");
  return `${sorted(productIds)}|${sorted(categoryIds)}`;
}
